import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  RadioOption,
  RadioOptionGroup,
  Select,
} from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import type { MeetingSource } from '@wundo/shared/calendar.ts';
import { fetchMeetings } from '@/lib/meetings.ts';
import {
  addFollowException,
  type NewMeetingFollowException,
} from '@/lib/meetingFollow.ts';
import { m } from '@/paraglide/messages.js';

/* How far ahead the meeting picker looks for something to point at. */
const PICKER_DAYS = 30;

type TargetKind = 'person' | 'series';
/* What the exception says. `no_follow` also means no report — see the page. */
type Rule = 'no_follow' | 'no_record';

const RULE_VALUES: Record<Rule, { follow: boolean | null; record: boolean | null }> = {
  no_follow: { follow: false, record: null },
  no_record: { follow: null, record: false },
};

/**
 * Switches Wundo or the notetaker off for a person or a recurring meeting.
 *
 * A meeting is picked from the user's own calendar rather than typed, so the
 * client never handles the provider id a correction is stored under — it sends
 * the meeting it can see and the server derives the rest.
 */
export function AddExceptionDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [targetKind, setTargetKind] = useState<TargetKind>('person');
  const [email, setEmail] = useState('');
  const [meetingId, setMeetingId] = useState('');
  const [rule, setRule] = useState<Rule>('no_record');

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const { data: meetings } = useQuery({
    queryKey: ['meetingPicker', user?.id],
    enabled: Boolean(user) && open,
    queryFn: () => {
      const from = new Date();
      const to = new Date(from.getTime() + PICKER_DAYS * 24 * 60 * 60 * 1000);
      return fetchMeetings(user!.id, from, to);
    },
  });

  const add = useMutation({
    mutationFn: addFollowException,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['meetingFollowExceptions'],
      });
      close();
    },
  });

  const close = () => {
    setOpen(false);
    setEmail('');
    setMeetingId('');
    add.reset();
  };

  const options = meetingOptions(meetings ?? []);
  const submittable =
    targetKind === 'person' ? email.trim().length > 0 : meetingId.length > 0;

  const submit = () => {
    const target: NewMeetingFollowException =
      targetKind === 'person'
        ? { target_kind: 'person', email: email.trim(), ...RULE_VALUES[rule] }
        : { target_kind: 'series', meeting_id: meetingId, ...RULE_VALUES[rule] };
    add.mutate(target);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
      <DialogTrigger render={<Button size='sm' variant='quiet' />}>
        {m.meetings_exception_add()}
      </DialogTrigger>
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>{m.meetings_exception_add()}</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-4 px-5 py-4'>
          <Field label={m.meetings_exception_target()}>
            <Select
              aria-label={m.meetings_exception_target()}
              className='w-full'
              value={targetKind}
              onChange={(event) => setTargetKind(event.target.value as TargetKind)}
              options={[
                { value: 'person', label: m.meetings_exception_target_person() },
                { value: 'series', label: m.meetings_exception_target_series() },
              ]}
            />
          </Field>

          {targetKind === 'person' ? (
            <Field
              label={m.meetings_exception_person_label()}
              hint={m.meetings_exception_person_hint()}
            >
              <Input
                type='email'
                aria-label={m.meetings_exception_person_label()}
                placeholder={m.meetings_exception_person_placeholder()}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
          ) : (
            <Field
              label={m.meetings_exception_series_label()}
              hint={m.meetings_exception_series_hint()}
            >
              {options.length > 0 ? (
                <Select
                  aria-label={m.meetings_exception_series_label()}
                  className='w-full'
                  value={meetingId}
                  onChange={(event) => setMeetingId(event.target.value)}
                  options={[{ value: '', label: '—' }, ...options]}
                />
              ) : (
                <p className='text-sm text-ink-muted'>
                  {m.meetings_exception_no_meetings()}
                </p>
              )}
            </Field>
          )}

          {/*
           * Not a `Field`: that is a `<label>`, which belongs to one control.
           * A group of cards gets a heading and its own `aria-label` instead.
           */}
          <div className='flex flex-col gap-1.5'>
            <span className='text-sm font-semibold text-ink'>
              {m.meetings_exception_rule()}
            </span>
            <RadioOptionGroup
              aria-label={m.meetings_exception_rule()}
              value={rule}
              onValueChange={setRule}
            >
              <RadioOption
                value='no_follow'
                label={m.meetings_exception_rule_support()}
                description={m.meetings_exception_rule_support_desc()}
              />
              <RadioOption
                value='no_record'
                label={m.meetings_exception_rule_notetaker()}
                description={m.meetings_exception_rule_notetaker_desc()}
              />
            </RadioOptionGroup>
          </div>

          {add.isError && (
            <p role='alert' className='text-sm text-danger'>
              {m.meetings_exception_failed()}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button size='sm' variant='quiet' onClick={close}>
            {m.meetings_exception_cancel()}
          </Button>
          <Button size='sm' disabled={!submittable || add.isPending} onClick={submit}>
            {m.meetings_exception_save()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className='flex flex-col gap-1.5'>
      <span className='text-sm font-semibold text-ink'>{label}</span>
      {children}
      {hint && <span className='text-sm text-ink-muted'>{hint}</span>}
    </label>
  );
}

/*
 * One entry per meeting, keeping the soonest of each title.
 *
 * A recurring meeting is on the calendar once per occurrence, and picking any
 * of them means the same series — so listing them all would be the same choice
 * repeated. Two different meetings that happen to share a title collapse into
 * one entry, which is the cost of not showing "Weekly" twelve times.
 *
 * A meeting the user wrote down themselves is left out: Wundo helps with it
 * because they asked for it, and there is nothing there to switch off.
 */
function meetingOptions(
  meetings: { id: string; title: string | null; source: MeetingSource }[],
): { value: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const meeting of meetings) {
    const title = meeting.title ?? '';
    if (!title || seen.has(title) || meeting.source === 'manual') continue;
    seen.set(title, meeting.id);
  }
  return [...seen].map(([title, id]) => ({ value: id, label: title }));
}
